// Canvas-composite pipeline for the `big3-signature-events` Block
// (P-037.24 / D-132; reworked at P-037.26 / D-134; @2x retina + logo
// cover-crop added at P-037.29 / D-137 §item 5; inset-shadow algorithm
// switched at post-Phase-Close §item G). Parallels D-121's
// `image-resize.ts` — same browser APIs (`Image` + `OffscreenCanvas.
// convertToBlob`), same support-gate pattern (`isCompositeSupported`),
// same silent-fallback discipline at the caller. The output is a
// 380 × 300 PNG (canvas at @2x retina; the `<img>` tag renders at the
// declared @1x 190 × 150 — retina displays serve sharp pixels via
// downsampling) with the navy bar baked in at the boundary line and
// the Big 3 logo overlaid with a circular clip-mask.
//
// **Inset-shadow source-of-truth** lives in `./image-shadow.ts`
// (extracted at post-Phase-Close §item G). Both the upload-resize
// pipeline (when `ImageTarget.bakeInsetShadow` is set) and these
// composite pipelines share the same Gaussian-blur stroke algorithm
// + 0.25 default opacity. Drift between the two consumers would be
// visible side-by-side in the same email — the shared module locks it
// down.
//
// **@2x retina convention (P-037.29 D-137 §item 5).** All canvas
// dimensions + drawing coordinates double from the @1x layout values.
// The `<img>` tag's width/height attrs (driven by `image-dimensions.ts`)
// stay at @1x — staff sees a 190 × 150 card, but the underlying PNG
// is 380 × 300 so retina displays serve crisp source. Matches D-121's
// retina default for all other image uploads — composites were the
// lone @1x exception. Bandwidth cost: minor (single composite per
// Block instance × maybe 5 composites per chapter Email).
//
// **Why 190 × 150 (was 190 × 110 pre-D-134).** Banks's PSD reference
// at the P-037.26 kickoff: the Big 3 logo straddles the navy bar /
// image boundary 50/50 (logo center on the line, not the top edge).
// That extends the logo's top edge 40 px above the navy bar (was
// flush with the canvas top at D-132); the canvas grows up by 40 px
// to accommodate the overhang. Logo size (100 × 100) + navy thickness
// (10 px) + event image height (100 px) all unchanged from D-132 —
// only the overhang region is new. The 40 px overhang region is
// transparent on the rendered PNG, so the email body bg / Big3 outer
// table tint shows through outside the circular logo mask.
//
// **Why a single composite img instead of layered HTML.** Email-client
// HTML/CSS doesn't reliably support `position: absolute` over an
// image. A composite PNG renders identically across every target
// client. The cost is one extra upload per Big 3 card on source
// change; chapter-scale this is a few uploads per Email.
//
// **Circular clip-mask (P-037.26 D-134).** Canvas `arc()` + `clip()`
// before the `drawImage(logo)` call masks the logo to a circle. The
// pattern wraps in `ctx.save()` / `ctx.restore()` so the clip path
// doesn't leak to subsequent draws (none exist today; defensive for
// future composite stages). Chapter logos can stay square in source —
// the mask renders them as round badges. For non-square sources,
// `containDestRect` fits the logo inside the 100 × 100 box first;
// the circular mask is at the 100 × 100 box boundary, so a wide-
// aspect logo shows letterbox transparency at top/bottom inside the
// circle (acceptable; same compromise as `object-fit: contain` in
// CSS).
//
// **CORS + Canvas.** `image.crossOrigin = 'anonymous'` MUST be set
// BEFORE assigning `image.src` — setting after src is set is a no-op.
// Firebase Storage's CORS config (D-119 + `infra/cors.json`) covers
// anonymous reads from `*.web.app` + the chapter subdomain. If a
// non-Storage URL CORS-fails, the source-load arm rejects with a
// descriptive error and the caller surfaces a toast.
//
// **Animated GIFs.** `image.src = url` reads the first frame; the
// composite captures that frame. Documented constraint — same as the
// resize pipeline (D-121).

import { applyInsetShadow, DEFAULT_INSET_SHADOW_BLUR_PX } from './image-shadow'

export interface CompositeInput {
  /** Event image source URL. When undefined, falls back to
   *  `fallbackEventImageUrl`. */
  readonly eventImageUrl: string | undefined
  /** Big 3 logo source URL. When undefined, falls back to
   *  `fallbackLogoUrl`. */
  readonly big3LogoUrl: string | undefined
  /** Chapter fallback for a missing event image (e.g.,
   *  `/fallback-image.png`). */
  readonly fallbackEventImageUrl: string
  /** Chapter fallback for a missing Big 3 logo (e.g.,
   *  `/fallback-logo.png`). */
  readonly fallbackLogoUrl: string
}

export interface CompositeOutput {
  readonly blob: Blob
  readonly width: number
  readonly height: number
}

// Canvas layout constants (D-132; updated at D-134; @2x at D-137 §5).
// @1x logical layout:
// - 190 × 150 outer footprint.
// - Navy bar at y = 40..50 (top coincides with logo's vertical center).
// - Event image at y = 50..150 (190 × 100 region).
// - Logo at (45, 0) sized 100 × 100, vertical center at y = 50.
//
// @2x physical canvas (doubled values below): all coordinates × 2.
const RETINA_SCALE = 2
const COMPOSITE_WIDTH = 190 * RETINA_SCALE // 380
const COMPOSITE_HEIGHT = 150 * RETINA_SCALE // 300
const NAVY_BAR_HEIGHT = 10 * RETINA_SCALE // 20
const EVENT_IMAGE_HEIGHT = 100 * RETINA_SCALE // 200
const LOGO_OVERHANG_ABOVE_NAVY = COMPOSITE_HEIGHT - NAVY_BAR_HEIGHT - EVENT_IMAGE_HEIGHT // 80
const NAVY_BAR_Y = LOGO_OVERHANG_ABOVE_NAVY // 80
const EVENT_IMAGE_Y = NAVY_BAR_Y + NAVY_BAR_HEIGHT // 100
const LOGO_SIZE = 100 * RETINA_SCALE // 200
const LOGO_X = Math.round((COMPOSITE_WIDTH - LOGO_SIZE) / 2) // 90
const LOGO_Y = 0
const LOGO_CENTER_X = LOGO_X + LOGO_SIZE / 2 // 190
const LOGO_CENTER_Y = LOGO_Y + LOGO_SIZE / 2 // 100 — navy/image boundary line
const LOGO_RADIUS = LOGO_SIZE / 2 // 100
// Matches `tokens.color['accent-navy-deep']` exactly — duplicating the
// hex literal avoids pulling the design-token surface into this pure
// lib module. The token's value is locked at D-118 / D-131; a future
// brand-color change ripples to a single edit here.
const NAVY_HEX = '#041e42'

/**
 * Returns true when the browser supports the composite pipeline.
 * False on Safari ≤14 / Firefox ≤78 / IE — any environment missing
 * `OffscreenCanvas.prototype.convertToBlob`. Callers use this gate to
 * fall back to rendering the event image WITHOUT the logo overlay (the
 * empty-state arm in `Big3EventCard`), matching the silent-fallback
 * discipline from the resize pipeline (D-121).
 */
export function isCompositeSupported(): boolean {
  if (typeof OffscreenCanvas !== 'function') return false
  return typeof OffscreenCanvas.prototype.convertToBlob === 'function'
}

/**
 * Composite an event image + Big 3 logo into a single 190 × 150 PNG
 * (D-134). Layout:
 * - y = 0..40: overhang region (transparent except where logo top-
 *   half draws).
 * - y = 40..50: navy bar (10 px).
 * - y = 50..150: event image (190 × 100, cover-cropped centered).
 * - y = 0..100, x = 45..145: logo (100 × 100), circular-clipped,
 *   centered on the navy/image boundary line.
 *
 * Falls back per-source independently: missing event image → fallback
 * event PNG; missing logo → fallback logo PNG. Always produces a
 * composite; the only way to get no composite is to not call this
 * function.
 *
 * Event image renders with `object-fit: cover` semantics (crop
 * centered for non-1.9:1 sources). Logo renders with circular clip-
 * mask + `object-fit: contain` semantics inside the 100 × 100 cell
 * (D-134).
 *
 * Rejects when either source image fails to load (CORS error, 404,
 * malformed image). Caller surfaces a toast + offers retry.
 *
 * Throws synchronously if `isCompositeSupported() === false`. Guard at
 * the call site.
 */
export async function compositeBig3Image(input: CompositeInput): Promise<CompositeOutput> {
  const eventSrc = input.eventImageUrl ?? input.fallbackEventImageUrl
  const logoSrc = input.big3LogoUrl ?? input.fallbackLogoUrl
  const [eventImg, logoImg] = await Promise.all([loadImage(eventSrc), loadImage(logoSrc)])

  const canvas = new OffscreenCanvas(COMPOSITE_WIDTH, COMPOSITE_HEIGHT)
  const ctx = canvas.getContext('2d')
  if (ctx === null) {
    throw new Error('Canvas 2D context unavailable.')
  }

  // 1. Fill the 10 px navy bar at y = 40..50 (its top coincides with
  //    the logo's vertical-center line). fillRect honors the canvas's
  //    default transparent background everywhere outside this rect, so
  //    the overhang region above (y = 0..40) stays transparent unless
  //    the logo's top half draws over it in step 3.
  ctx.fillStyle = NAVY_HEX
  ctx.fillRect(0, NAVY_BAR_Y, COMPOSITE_WIDTH, NAVY_BAR_HEIGHT)

  // 2. Draw event image with cover semantics (crop centered). Lands
  //    at y = EVENT_IMAGE_Y (50) below the navy bar.
  const eventCrop = coverSourceRect(
    eventImg.width,
    eventImg.height,
    COMPOSITE_WIDTH / EVENT_IMAGE_HEIGHT,
  )
  ctx.drawImage(
    eventImg,
    eventCrop.sx,
    eventCrop.sy,
    eventCrop.sw,
    eventCrop.sh,
    0,
    EVENT_IMAGE_Y,
    COMPOSITE_WIDTH,
    EVENT_IMAGE_HEIGHT,
  )

  // 2b. Bake the inset-shadow effect ONLY on the event-image rect
  //     (P-037.29 D-137 §item 4 revised — was a cell-level box-shadow
  //     pre-revision; Banks's correction: bake into the PNG so the
  //     shadow is part of the image content, no cell-wrap padding ring
  //     needed at render time). Applied AFTER the event image draw +
  //     BEFORE the logo draw so the logo's circle sits ON TOP of the
  //     shadowed image (logo not darkened at its edges). Post-Phase-
  //     Close §item G — algorithm switched from 4 directional gradients
  //     to a Gaussian-blurred clipped stroke (no corner doubling);
  //     opacity dropped 0.35 → 0.25.
  applyInsetShadow(ctx, 0, EVENT_IMAGE_Y, COMPOSITE_WIDTH, EVENT_IMAGE_HEIGHT, {
    blurPx: DEFAULT_INSET_SHADOW_BLUR_PX * RETINA_SCALE,
  })

  // 3. Draw logo with circular clip-mask (D-134) + cover semantics
  //    (P-037.29 D-137 §item 5 — was contain semantics pre-D-137; the
  //    contain treatment letterboxed tall/narrow logos inside the
  //    circle, exposing transparent stripes that masked through to
  //    the event image behind). Cover fills the 200 × 200 box edge-
  //    to-edge (shorter side scales to 200 px; longer side center-
  //    crops). Square chapter logos: cover == contain (no crop).
  //    Non-square logos: center-crop. The circular clip then masks
  //    the box to a circle. `save()` + `clip()` + `restore()`
  //    localizes the clip path so it doesn't leak to subsequent
  //    draws. `arc()` carves the logo's circumscribed circle (radius
  //    100 at center (190, 100) @2x).
  ctx.save()
  ctx.beginPath()
  ctx.arc(LOGO_CENTER_X, LOGO_CENTER_Y, LOGO_RADIUS, 0, 2 * Math.PI)
  ctx.clip()
  // Cover-crop the source to a 1:1 aspect (square box); the result fills
  // the LOGO_SIZE × LOGO_SIZE destination edge-to-edge.
  const logoCrop = coverSourceRect(logoImg.width, logoImg.height, 1)
  ctx.drawImage(
    logoImg,
    logoCrop.sx,
    logoCrop.sy,
    logoCrop.sw,
    logoCrop.sh,
    LOGO_X,
    LOGO_Y,
    LOGO_SIZE,
    LOGO_SIZE,
  )
  ctx.restore()

  const blob = await canvas.convertToBlob({ type: 'image/png' })
  return { blob, width: COMPOSITE_WIDTH, height: COMPOSITE_HEIGHT }
}

/**
 * Load a same-origin or CORS-permitted image as an `HTMLImageElement`.
 * `crossOrigin = 'anonymous'` MUST be set before assigning `src` — the
 * spec only consults the attribute at the moment fetching starts.
 * Rejects on `error` event (network failure, malformed image, CORS
 * denial). Resolves with the loaded image once `naturalWidth > 0`.
 *
 * Animated GIFs decode the first frame only — the underlying browser
 * behavior, not a deliberate choice; matches the resize pipeline (D-121).
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Could not load image: ${src}`))
    img.src = src
  })
}

// (`applyInsetShadow` + `DEFAULT_INSET_SHADOW_BLUR_PX` hoisted to
//  `./image-shadow.ts` at post-Phase-Close §item G — see that module's
//  header for the algorithm rationale.)

/**
 * Cover-crop source rect: pick a centered region of the source whose
 * aspect ratio matches `targetAspect`. The caller scales the result
 * onto the destination rect.
 */
export function coverSourceRect(
  srcW: number,
  srcH: number,
  targetAspect: number,
): { sx: number; sy: number; sw: number; sh: number } {
  const srcAspect = srcW / srcH
  if (srcAspect > targetAspect) {
    // Source is wider than target — crop horizontally.
    const sw = srcH * targetAspect
    const sx = (srcW - sw) / 2
    return { sx, sy: 0, sw, sh: srcH }
  }
  // Source is taller (or equal) — crop vertically.
  const sh = srcW / targetAspect
  const sy = (srcH - sh) / 2
  return { sx: 0, sy, sw: srcW, sh }
}

/**
 * Contain-fit destination rect: scale the source down to fit inside
 * `(boxW, boxH)` preserving aspect ratio, then center within the box.
 * Returns the destination offset + dimensions; the caller adds the
 * box's origin offset before passing to drawImage.
 *
 * Never upscales — a source already smaller than the box stays at
 * source dimensions and centers within the box.
 */
export function containDestRect(
  srcW: number,
  srcH: number,
  boxW: number,
  boxH: number,
): { dx: number; dy: number; dw: number; dh: number } {
  const scale = Math.min(1, boxW / srcW, boxH / srcH)
  const dw = srcW * scale
  const dh = srcH * scale
  const dx = (boxW - dw) / 2
  const dy = (boxH - dh) / 2
  return { dx, dy, dw, dh }
}

// ── Big 3 hero image composite (P-037.28 / D-136; reworked at P-037.29
//    / D-137 §item 6 with @2x retina + new 620 × 255 layout) ──────────
//
// Sibling pipeline to `compositeBig3Image` above. Same browser APIs
// (Image + OffscreenCanvas), same CORS + animated-GIF + support-gate
// discipline. Different layout math (per Banks's planning-session
// design):
//
// @1x logical layout (620 × 255):
// - y = 0..85: transparent above the navy bar (hosts the logo's upper
//   half via overhang).
// - y = 85..95: navy bar 620 × 10 across full canvas width.
// - y = 95..255: event image 620 × 160 across full canvas width.
// - x = 215..405, y = 0..190: logo 190 × 190 — horizontally centered
//   (`(620 - 190) / 2 = 215`), top of canvas. Vertical center at
//   y = 95 → the logo's center sits exactly on the navy/image boundary.
// - Z-order: event image → navy bar → logo. Logo's upper 85 px overhangs
//   above the navy bar (transparent canvas), middle 10 px covers the
//   center of the navy bar, lower 95 px overlaps the top of the event
//   image. Outside the circular clip, the underlying layer shows
//   through.
//
// @2x physical canvas (doubled values): 1240 × 510. All coordinates × 2.
//
// Pre-D-137 layout (525 × 190 with left-anchored 190 × 190 logo at
// (0, 0)) replaced entirely. Existing 525 × 190 composites stay in
// Firebase Storage; next staff edit of either source field generates
// a new 620 × 255 composite (overwrites compositeImageUrl).

const HERO_CANVAS_WIDTH = 620 * RETINA_SCALE // 1240
const HERO_CANVAS_HEIGHT = 255 * RETINA_SCALE // 510
const HERO_EVENT_IMG_WIDTH = 620 * RETINA_SCALE // 1240
const HERO_EVENT_IMG_HEIGHT = 160 * RETINA_SCALE // 320
const HERO_NAVY_BAR_HEIGHT = 10 * RETINA_SCALE // 20
const HERO_LOGO_SIZE = 190 * RETINA_SCALE // 380
// Logo horizontally centered: (620 - 190) / 2 = 215 @1x → × 2 = 430 @2x.
const HERO_LOGO_X = Math.round((HERO_CANVAS_WIDTH - HERO_LOGO_SIZE) / 2) // 430
const HERO_LOGO_Y = 0
// Vertical center at y = 95 @1x (190 / 2) — coincides with the navy/
// image boundary line.
const HERO_LOGO_CENTER_X = HERO_LOGO_X + HERO_LOGO_SIZE / 2 // 620
const HERO_LOGO_CENTER_Y = HERO_LOGO_Y + HERO_LOGO_SIZE / 2 // 190
const HERO_LOGO_RADIUS = HERO_LOGO_SIZE / 2 // 190
// Navy bar at y = 85 @1x (logo center 95 − navy half-height 5 = 90... no,
// navy bar TOP must sit at y = LOGO_CENTER_Y − HALF_NAVY_HEIGHT so the
// boundary line is the bar's center). NAVY_BAR_Y = LOGO_CENTER_Y −
// HERO_NAVY_BAR_HEIGHT / 2 = 190 − 10 = 180 @2x = 90 @1x. Hmm wait
// Banks's spec says navy bar at y = 85 @1x and image at y = 95 @1x —
// so navy bar TOP is 10 px above the event image TOP (which is at the
// boundary line). That makes navy bar's BOTTOM (95) = image TOP (95) =
// boundary line. So logo's vertical center at y = 95 sits ON the
// boundary line = the navy/image bottom edge.
//
// Coordinates @2x:
// - HERO_NAVY_BAR_Y = 85 @1x × 2 = 170
// - HERO_EVENT_IMG_Y = 95 @1x × 2 = 190
// - HERO_LOGO_CENTER_Y = 95 @1x × 2 = 190 (matches event image top)
const HERO_NAVY_BAR_Y = 85 * RETINA_SCALE // 170
const HERO_EVENT_IMG_Y = 95 * RETINA_SCALE // 190

export interface HeroCompositeOutput {
  readonly blob: Blob
  readonly width: number
  readonly height: number
}

/**
 * Composite a big-3-hero-image event image + logo into a single
 * 1240 × 510 PNG (@2x retina of the 620 × 255 @1x layout). Layout @1x:
 * - y = 0..85: transparent above the navy bar.
 * - y = 85..95: navy bar (620 × 10).
 * - y = 95..255: event image (620 × 160, cover-cropped centered for
 *   non-3.875:1 sources).
 * - x = 215..405, y = 0..190: logo (190 × 190), circular-clipped at
 *   center (310, 95) radius 95. Center sits on the navy/image
 *   boundary line.
 *
 * Z-order: event image → navy bar → logo. Outside the logo's circle,
 * the underlying layer (transparent canvas above the navy bar; navy
 * bar in the middle 10 px; event image below) shows through.
 *
 * Falls back per-source independently: missing event image →
 * fallback event PNG; missing logo → fallback logo PNG. Always
 * produces a composite; the only way to get no composite is to not
 * call this function (the Block's auto-hide path covers the
 * both-empty case).
 *
 * Throws synchronously if `isCompositeSupported() === false`. Guard
 * at the call site.
 */
export async function compositeBigHero(input: CompositeInput): Promise<HeroCompositeOutput> {
  const eventSrc = input.eventImageUrl ?? input.fallbackEventImageUrl
  const logoSrc = input.big3LogoUrl ?? input.fallbackLogoUrl
  const [eventImg, logoImg] = await Promise.all([loadImage(eventSrc), loadImage(logoSrc)])

  const canvas = new OffscreenCanvas(HERO_CANVAS_WIDTH, HERO_CANVAS_HEIGHT)
  const ctx = canvas.getContext('2d')
  if (ctx === null) {
    throw new Error('Canvas 2D context unavailable.')
  }

  // 1. Draw event image with cover semantics (crop centered). Lands at
  //    y = HERO_EVENT_IMG_Y — fills 620 × 160 at @1x (1240 × 320 @2x).
  //    Anything below the navy bar position is the event image's
  //    territory.
  const eventCrop = coverSourceRect(
    eventImg.width,
    eventImg.height,
    HERO_EVENT_IMG_WIDTH / HERO_EVENT_IMG_HEIGHT,
  )
  ctx.drawImage(
    eventImg,
    eventCrop.sx,
    eventCrop.sy,
    eventCrop.sw,
    eventCrop.sh,
    0,
    HERO_EVENT_IMG_Y,
    HERO_EVENT_IMG_WIDTH,
    HERO_EVENT_IMG_HEIGHT,
  )

  // 1b. Bake the inset-shadow effect ONLY on the event-image rect
  //     (P-037.29 D-137 §item 4 revised — Banks's correction: bake
  //     into the PNG so the shadow is part of the image content, no
  //     cell-wrap padding ring needed at render time). Applied AFTER
  //     the event image draw + BEFORE the navy bar + logo overlays so
  //     the navy bar and logo's circle sit ON TOP of the shadowed
  //     image (navy + logo not darkened at their edges). Post-Phase-
  //     Close §item G — algorithm switched + opacity dropped 0.35 →
  //     0.25 (see `./image-shadow.ts` header).
  applyInsetShadow(ctx, 0, HERO_EVENT_IMG_Y, HERO_EVENT_IMG_WIDTH, HERO_EVENT_IMG_HEIGHT, {
    blurPx: DEFAULT_INSET_SHADOW_BLUR_PX * RETINA_SCALE,
  })

  // 2. Draw 10 px navy bar at y = HERO_NAVY_BAR_Y — sits directly above
  //    the event image, its bottom edge flush with the image's top edge.
  //    Banks's intent: a hairline navy stripe marking the boundary.
  ctx.fillStyle = NAVY_HEX
  ctx.fillRect(0, HERO_NAVY_BAR_Y, HERO_CANVAS_WIDTH, HERO_NAVY_BAR_HEIGHT)

  // 3. Draw logo with circular clip-mask + cover semantics (P-037.29
  //    D-137 §item 5 — square chapter logos cover-fill the box edge-
  //    to-edge; non-square sources center-crop). Same save/clip/
  //    restore idiom as compositeBig3Image. Logo's center sits on
  //    the navy/image boundary line (y = 95 @1x): top 85 px overhang
  //    above navy, middle 10 px over navy, bottom 95 px over event
  //    image. Outside the circle, those underlying layers show through.
  ctx.save()
  ctx.beginPath()
  ctx.arc(HERO_LOGO_CENTER_X, HERO_LOGO_CENTER_Y, HERO_LOGO_RADIUS, 0, 2 * Math.PI)
  ctx.clip()
  const logoCrop = coverSourceRect(logoImg.width, logoImg.height, 1)
  ctx.drawImage(
    logoImg,
    logoCrop.sx,
    logoCrop.sy,
    logoCrop.sw,
    logoCrop.sh,
    HERO_LOGO_X,
    HERO_LOGO_Y,
    HERO_LOGO_SIZE,
    HERO_LOGO_SIZE,
  )
  ctx.restore()

  const blob = await canvas.convertToBlob({ type: 'image/png' })
  return { blob, width: HERO_CANVAS_WIDTH, height: HERO_CANVAS_HEIGHT }
}
